import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Layouts
import MainLayout from "layouts/Main";

// Components
import {
  Button,
  InputField,
  Link,
  SupportingMessage,
} from "@octopus-investments/deep-sea-components";
import PasswordField from "components/Inputs/PasswordField/PasswordField";

// Actions
import { signInTwoFactor } from "store/slices/accountSlice";
import { setContent, setRedirectUrl } from "store/slices/applicationSlice";
import { setTwoFactorLoginButtonLoader } from "store/slices/loadersSlice";

// Tools
import { identifyUserInTrackingSystem, getStatusForAmpli } from "utils/helpers";
import { MFA_AUTH_CODE_TOOLTIP, VALIDATION_ERRORS } from "utils/constants";

// Tracking
import { ampli } from "ampli";

// Styles
import "./LoginPage.scss";

export default function TwoFactorLoginPage() {
  const { isTwoFactorLoginButtonLoader } = useSelector(({ loaders }) => loaders);
  const { email, password, isTwoFactorEnabled, redirectUrl } = useSelector(
    ({ application }) => application,
  );

  const [verificationCode, setVerificationCode] = useState("");
  const [showError, setShowError] = useState(false);

  // Redux
  const reduxDispatch = useDispatch();

  function handleCodeChange(e) {
    setVerificationCode(e.value);
  }

  function handleVerificationCodeKeyPress(e) {
    if (e.which === 13) handleLoginButton();
  }
  const renderHelpLink = () => {
    const handleOpenBeaconAskSection = () => {
      window.Beacon("navigate", "/ask/");
      window.Beacon("open");

      ampli.embeddedBeaconClicked({
        external_link_clicked: "beacon_mfa_contact",
        location: "di_login_page",
      });
    };

    return (
      <Link isInline stopPropagation onLinkClick={handleOpenBeaconAskSection}>
        Get in touch
      </Link>
    );
  };

  function handleBackButton() {
    ampli.backButtonClicked({
      di_email_entered: email,
      location: "di_login_page",
    });

    reduxDispatch(setContent("login"));
  }

  const handleAuthCodeTooltip = () => {
    ampli.tooltipViewed({
      location: "di_login_page",
      tooltip_type: "authentication_code",
    });
  };

  function handleLoginButton() {
    if (isTwoFactorLoginButtonLoader) return;

    reduxDispatch(setTwoFactorLoginButtonLoader(true));

    reduxDispatch(signInTwoFactor({ email, password, verificationCode })).then(result => {
      const { isTemporaryLocked, status, success } = result.payload;

      ampli.loginIntended({
        di_email_entered: email,
        di_status: getStatusForAmpli({ isTemporaryLocked, status }),
        mfa_status: isTwoFactorEnabled,
      });

      if (success) {
        identifyUserInTrackingSystem({ source: "two-factor-login-page" });

        ampli.loginCompleted({
          di_status: getStatusForAmpli({ isTemporaryLocked, status }),
          email,
          mfa_status: isTwoFactorEnabled,
        });

        if (redirectUrl) {
          window.location.href = redirectUrl;
          setRedirectUrl("");
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const isRedirect = urlParams.has("returnUrl");
        const returnUrl = isRedirect
          ? urlParams.get("returnUrl")
          : process.env.REACT_APP_WELCOME_BTN_URL;
        window.location.href = returnUrl;
      } else {
        setShowError(true);
        return reduxDispatch(setTwoFactorLoginButtonLoader(false));
      }
    });
  }

  return (
    <MainLayout>
      <div className="login-content">
        <div className="login-form">
          <h2 className="coheadline navyBlue ta-l">
            Enter the code from your authenticator or passwords app
          </h2>
          <p className="subtitle">
            Please verify your identity by entering the 6-digit code from your chosen authenticator
            app on your mobile phone. Open the app to view the code and enter it below. You will not
            receive a text message.
          </p>

          <div className="enter-your-password-block ta-l">
            <InputField
              autoFocus
              label="Verification code"
              className="client-code"
              disabled={isTwoFactorLoginButtonLoader}
              id="loginPageCodeInput"
              name="code"
              onChange={handleCodeChange}
              onKeyPress={handleVerificationCodeKeyPress}
              value={verificationCode}
              placeholder="Enter your 6-digit code here"
            />
            {showError && (
              <SupportingMessage status="error" message={VALIDATION_ERRORS["wrongCode"]} />
            )}

            <div className="information-block">
              <p>Need help? {renderHelpLink()}</p>
            </div>
            <div className="buttons-block">
              <Button label="Back" onClick={handleBackButton} variant="outline" />
              <Button
                isLoading={isTwoFactorLoginButtonLoader}
                label="Login"
                onClick={handleLoginButton}
                variant="solid"
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
